import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Card from '../templates/ui/Card'
import Button from '../templates/ui/Button'
import CenterPopup from '../templates/ui/CenterPopup'
import UserAvatar from '../templates/ui/UserAvatar'
import { muteluApi } from '../services/muteluApi'
import { buildApiErrorPopupTemplate } from '../templates/popupTemplate'
import { logPageApiCall, logPageApiError, logPageApiResult } from '../utils/apiDebugLogger'
import ticketGoldImage from '../../../assets/images/game-mutelu/ticket-gold.png'
import ticketSilverImage from '../../../assets/images/game-mutelu/ticket-silver.png'
import ticketBronzeImage from '../../../assets/images/game-mutelu/ticket-broze.png'
import './Dashboard.css'

const STG_COUPON_URL = 'https://linebc-dmkt-stg.web.app/my-coupons'
const PROD_COUPON_URL = 'https://linebc.bigc.co.th/my-coupons'
const DEFAULT_DASHBOARD_DATA = {
  top_players: [],
  groups: []
}

const normalizeMessage = (value, fallback = '') => {
  if (typeof value === 'string') return value
  if (value == null) return fallback
  if (typeof value === 'object') {
    if (typeof value.message === 'string') return value.message
    try {
      return JSON.stringify(value)
    } catch {
      return fallback
    }
  }
  return String(value)
}

const toSafeNumber = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const formatNumber = (value) => toSafeNumber(value).toLocaleString('th-TH')

const getPlayerName = (item) =>
  item?.display_name ||
  item?.player_name ||
  item?.line_name ||
  item?.name ||
  ''

const getPlayerScore = (item) =>
  toSafeNumber(
    item?.score ??
      item?.total_score ??
      item?.points ??
      item?.weighted_score ??
      0
  )

export default function Dashboard() {
  const [dashboardData, setDashboardData] = useState(DEFAULT_DASHBOARD_DATA)
  const [isLoading, setIsLoading] = useState(true)
  const [apiPopupTemplate, setApiPopupTemplate] = useState(null)
  const [activeTab, setActiveTab] = useState('my-party')
  const couponsUrl = useMemo(() => {
    if (typeof window === 'undefined') return STG_COUPON_URL
    const host = String(window.location.hostname || '').trim().toLowerCase()
    if (host === 'linebc.bigc.co.th') return PROD_COUPON_URL
    return STG_COUPON_URL
  }, [])

  useEffect(() => {
    let mounted = true

    const loadTopPlayers = async () => {
      setIsLoading(true)
      try {
        logPageApiCall('Dashboard', 'getDashboardLeaderboard', { limit: 20, force: true })
        const response = await muteluApi.getDashboardLeaderboard(20, true)
        logPageApiResult('Dashboard', 'getDashboardLeaderboard', response)
        const data =
          response?.data && typeof response.data === 'object'
            ? response.data
            : response || DEFAULT_DASHBOARD_DATA
        if (!mounted) return
        setDashboardData({
          ...DEFAULT_DASHBOARD_DATA,
          ...data
        })
      } catch (error) {
        logPageApiError('Dashboard', 'getDashboardLeaderboard', error)
        if (!mounted) return
        setApiPopupTemplate(
          buildApiErrorPopupTemplate({
            title: 'โหลดผู้เล่นไม่สำเร็จ',
            subtitle: 'หน้า User',
            message: normalizeMessage(
              error?.data?.message || error?.message,
              'ไม่สามารถโหลดข้อมูลผู้เล่นได้'
            )
          })
        )
      } finally {
        if (mounted) setIsLoading(false)
      }
    }

    loadTopPlayers()
    return () => {
      mounted = false
    }
  }, [])

  const topPlayers = useMemo(() => {
    const raw = Array.isArray(dashboardData?.top_players) ? dashboardData.top_players : []
    return raw.slice(0, 20).map((item, index) => ({
      rank: toSafeNumber(item?.rank) || index + 1,
      playerId: toSafeNumber(item?.player_id),
      name: getPlayerName(item),
      score: getPlayerScore(item),
      pictureUrl: String(item?.picture_url || ''),
      gold: toSafeNumber(item?.gold),
      silver: toSafeNumber(item?.silver),
      bronze: toSafeNumber(item?.bronze)
    }))
  }, [dashboardData])

  const myPartyGroup = useMemo(() => {
    const groups = Array.isArray(dashboardData?.groups) ? dashboardData.groups : []
    const lineId = String(localStorage.getItem('userId') || '').trim()
    if (!lineId || !groups.length) return null

    const statusRank = { active: 2, completed: 1 }
    const joinedGroups = groups
      .filter((group) =>
        Array.isArray(group?.members)
          ? group.members.some((member) => String(member?.line_id || '').trim() === lineId)
          : false
      )
      .sort((left, right) => {
        const leftStatus = String(left?.status || '').toLowerCase()
        const rightStatus = String(right?.status || '').toLowerCase()
        const leftPriority = statusRank[leftStatus] || 0
        const rightPriority = statusRank[rightStatus] || 0
        if (rightPriority !== leftPriority) return rightPriority - leftPriority
        const leftCreated = new Date(left?.created_at || 0).getTime()
        const rightCreated = new Date(right?.created_at || 0).getTime()
        if (rightCreated !== leftCreated) return rightCreated - leftCreated
        return toSafeNumber(right?.group_id) - toSafeNumber(left?.group_id)
      })

    return joinedGroups[0] || null
  }, [dashboardData])

  const myPartyPlayers = useMemo(() => {
    const members = Array.isArray(myPartyGroup?.members) ? myPartyGroup.members : []
    return members
      .map((member) => ({
        playerId: toSafeNumber(member?.player_id),
        rank: 0,
        name: getPlayerName(member),
        score: toSafeNumber(member?.score),
        pictureUrl: String(member?.picture_url || ''),
        gold: toSafeNumber(member?.gold),
        silver: toSafeNumber(member?.silver),
        bronze: toSafeNumber(member?.bronze),
        isOwner: Boolean(member?.is_owner)
      }))
      .sort((left, right) => {
        if (right.score !== left.score) return right.score - left.score
        if (right.gold !== left.gold) return right.gold - left.gold
        if (right.silver !== left.silver) return right.silver - left.silver
        if (right.bronze !== left.bronze) return right.bronze - left.bronze
        return String(left.name || '').localeCompare(String(right.name || ''))
      })
      .map((item, index) => ({
        ...item,
        rank: index + 1
      }))
  }, [myPartyGroup])

  const activePlayers = activeTab === 'my-party' ? myPartyPlayers : topPlayers
  const activeTabTitle =
    activeTab === 'my-party' ? 'คะแนนในปาร์ตี้เรา' : 'รายชื่อผู้ชนะทั้งหมดที่ร่วมเล่นเกม'
  const activeTabEmptyMessage =
    activeTab === 'my-party' ? 'ยังไม่พบข้อมูลปาร์ตี้ของคุณ' : 'ยังไม่มีข้อมูลผู้เล่น'

  return (
    <section className="mobile-page mutelu-dashboard-page mx-auto w-full max-w-md">
      <Card className="mobile-panel mutelu-dashboard-card">
        <div className="landing-header px-4 py-4 md:px-6">
          <p className="landing-badge">Big C Receipt Mutelu</p>
          <h1 className="landing-title mt-1 md:text-3xl">บิลรู้ใจมูเตลู</h1>
          <p className="landing-subtitle mt-1">ภารกิจสะสมพลังมูแบบ Step ต่อ Step</p>
        </div>

        {isLoading ? <p className="dashboard-loading">กำลังโหลดข้อมูล...</p> : null}

        <h2 className="dashboard-section-title">ประกาศรายชื่อผู้ชนะ</h2>
        <div className="dashboard-tabs" role="tablist" aria-label="Leaderboard Tabs">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'my-party'}
            className={`dashboard-tab-btn ${activeTab === 'my-party' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('my-party')}
          >
           เช็กคะแนนปาร์ตี้
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'all-players'}
            className={`dashboard-tab-btn ${activeTab === 'all-players' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('all-players')}
          >
            ดูรายชื่อผู้ชนะ
          </button>
        </div>
        {activeTab === 'my-party' && myPartyGroup ? (
          <p className="dashboard-tab-meta">
            ห้องของคุณ: {String(myPartyGroup?.group_name || myPartyGroup?.group_code || '-')}
          </p>
        ) : null}
        {/* <p className="dashboard-active-tab-title">{activeTabTitle}</p> */}

        {!isLoading && activePlayers.length === 0 ? (
          <p className="dashboard-empty">{activeTabEmptyMessage}</p>
        ) : null}

        <div className="dashboard-user-list">
          {activePlayers.map((player) => (
            <article key={`player-${activeTab}-${player.playerId || 0}-${player.rank}-${player.name}`} className="dashboard-user-item">
              <p className="dashboard-user-rank">#{player.rank}</p>
              <div className="dashboard-user-profile">
                <div className="dashboard-user-avatar">
                  <UserAvatar
                    src={player.pictureUrl}
                    alt={player.name}
                    fallback="🙂"
                    imageClassName="dashboard-user-avatar-img"
                  />
                </div>
                <div className="dashboard-user-meta">
                  <p className="dashboard-user-name">
                    {player.name}
                    {player.isOwner ? ' (หัวหน้าตี้)' : ''}
                  </p>
                  <div className="dashboard-ticket-list">
                    <div
                      className="dashboard-ticket-item gold"
                      style={{ backgroundImage: `url(${ticketGoldImage})` }}
                      aria-label={`ตั๋วทอง ${formatNumber(player.gold)} ใบ`}
                    >
                      <span>{formatNumber(player.gold)}</span>
                    </div>
                    <div
                      className="dashboard-ticket-item silver"
                      style={{ backgroundImage: `url(${ticketSilverImage})` }}
                      aria-label={`ตั๋วเงิน ${formatNumber(player.silver)} ใบ`}
                    >
                      <span>{formatNumber(player.silver)}</span>
                    </div>
                    <div
                      className="dashboard-ticket-item bronze"
                      style={{ backgroundImage: `url(${ticketBronzeImage})` }}
                      aria-label={`ตั๋วทองแดง ${formatNumber(player.bronze)} ใบ`}
                    >
                      <span>{formatNumber(player.bronze)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <p className="dashboard-user-score">{formatNumber(player.score)} คะแนน</p>
            </article>
          ))}
        </div>

        <Link to="/game/mutelu/activity" className="dashboard-back-link">
          <Button className="landing-btn btn-uniform w-full">กลับหน้า Activity</Button>
        </Link>
        <a href={couponsUrl} className="dashboard-coupon-link">
          <Button className="landing-btn btn-uniform w-full">ไปหน้า My Coupons</Button>
        </a>
      </Card>

      <CenterPopup
        open={Boolean(apiPopupTemplate)}
        template={apiPopupTemplate}
        onCancel={() => setApiPopupTemplate(null)}
        onConfirm={() => setApiPopupTemplate(null)}
      />
    </section>
  )
}
